/**
 * VIEW CHECKOUT — جزئیات سفارش، آدرس، و نقشه
 */

import { formatPrice } from '../utils.js';
import { CONFIG }      from '../config.js';

let _orderTypeListenerAttached = false;
let _onOrderTypeChangeCb       = null;
let _mapInstance               = null;
let _mapMarker                 = null;

export function render(state, financials, callbacks) {
  _prefillReceiverInfo(state);
  _setupOrderTypeTabs(state.orderType, callbacks.onOrderTypeChange);
  _toggleDeliveryFields(state.orderType);
  _renderTotals(state.orderType, financials);
}

function _prefillReceiverInfo(state) {
  const phoneInput   = document.getElementById('receiver-phone');
  const nameInput    = document.getElementById('receiver-name');
  const addressInput = document.getElementById('receiver-address');
  if (phoneInput   && !phoneInput.value)   phoneInput.value   = state.receiverPhone   || '';
  if (nameInput    && !nameInput.value)    nameInput.value    = state.receiverName    || '';
  if (addressInput && !addressInput.value) addressInput.value = state.receiverAddress || '';
}

function _setupOrderTypeTabs(activeType, onChange) {
  const container = document.querySelector('.order-type-tabs');
  if (!container) return;
  if (!_orderTypeListenerAttached) {
    container.addEventListener('click', e => {
      const card = e.target.closest('.order-type-card');
      if (card && _onOrderTypeChangeCb) _onOrderTypeChangeCb(card.getAttribute('data-type'));
    });
    _orderTypeListenerAttached = true;
  }
  _onOrderTypeChangeCb = onChange;
  document.querySelectorAll('.order-type-card').forEach(card => {
    card.classList.toggle('active', card.getAttribute('data-type') === activeType);
  });
}

function _toggleDeliveryFields(orderType) {
  const addressSection = document.getElementById('checkout-address-section');
  const mapSection     = document.getElementById('checkout-map-section');
  const show           = orderType === 'delivery';
  addressSection?.classList.toggle('hidden', !show);
  mapSection?.classList.toggle('hidden', !show);
}

function _renderTotals(orderType, financials) {
  document.getElementById('checkout-summary-subtotal').textContent = formatPrice(financials.subtotal);

  const discRow = document.getElementById('checkout-summary-discount-row');
  if (financials.discount > 0) {
    discRow?.classList.remove('hidden');
    document.getElementById('checkout-summary-discount').textContent = `-${formatPrice(financials.discount)}`;
  } else {
    discRow?.classList.add('hidden');
  }

  const delivRow = document.getElementById('checkout-summary-delivery-row');
  if (orderType === 'delivery') {
    delivRow?.classList.remove('hidden');
    document.getElementById('checkout-summary-delivery').textContent =
      financials.deliveryFee === 0 ? 'رایگان' : formatPrice(financials.deliveryFee);
  } else {
    delivRow?.classList.add('hidden');
  }

  document.getElementById('checkout-summary-grandtotal').textContent = formatPrice(financials.grandTotal);
  document.getElementById('checkout-payable-total').textContent      = formatPrice(financials.grandTotal);
}

export function getFormValues() {
  return {
    name:    document.getElementById('receiver-name')?.value.trim()    || '',
    phone:   document.getElementById('receiver-phone')?.value.trim()   || '',
    address: document.getElementById('receiver-address')?.value.trim() || '',
  };
}

// ─── Map Picker ───────────────────────────────────────────────────────────────

/**
 * Initialize the map picker inside #checkout-map-section.
 * Supports Neshan (neshanmap.com) and Balad (balad.ir/sdk) APIs.
 * Falls back to a plain placeholder if no provider is configured.
 *
 * onLocationSelect({ lat, lng, address }) is called when user picks a point.
 */
export function initMapPicker(initialLocation, onLocationSelect) {
  const container = document.getElementById('map-picker-container');
  if (!container) return;

  const { provider, apiKey, defaultLat, defaultLng, defaultZoom } = CONFIG.MAP;
  const lat = initialLocation?.lat || defaultLat;
  const lng = initialLocation?.lng || defaultLng;

  if (provider === 'neshan') {
    _initNeshanMap(container, apiKey, lat, lng, defaultZoom, onLocationSelect);
  } else if (provider === 'balad') {
    _initBaladMap(container, apiKey, lat, lng, defaultZoom, onLocationSelect);
  } else {
    _initFallbackMap(container, lat, lng, onLocationSelect);
  }
}

function _initNeshanMap(container, apiKey, lat, lng, zoom, onSelect) {
  // Neshan map SDK (neshanmap.com)
  // Add <script src="https://static.neshan.org/sdk/openlayers/v1.1.7/ol.js"></script> to HTML
  if (!window.nmp_mapboxgl && !window.ol) {
    container.innerHTML = `<div class="map-placeholder"><p>SDK نشان بارگذاری نشده است</p></div>`;
    return;
  }

  // ── Real Neshan implementation ──
  // const map = new nmp_mapboxgl.Map({
  //   container: container.id || container,
  //   mapType: nmp_mapboxgl.Map.mapTypes.neshanVector,
  //   center: [lng, lat],
  //   zoom,
  //   mapKey: apiKey,
  //   poi: true,
  //   traffic: false,
  // });
  // const marker = new nmp_mapboxgl.Marker({ draggable: true })
  //   .setLngLat([lng, lat])
  //   .addTo(map);
  // marker.on('dragend', () => {
  //   const pos = marker.getLngLat();
  //   _reverseGeocode(pos.lat, pos.lng, apiKey).then(address => onSelect({ lat: pos.lat, lng: pos.lng, address }));
  // });
  // map.on('click', e => {
  //   marker.setLngLat([e.lngLat.lng, e.lngLat.lat]);
  //   _reverseGeocode(e.lngLat.lat, e.lngLat.lng, apiKey).then(address => onSelect({ lat: e.lngLat.lat, lng: e.lngLat.lng, address }));
  // });

  _initFallbackMap(container, lat, lng, onSelect); // placeholder until SDK is loaded
}

function _initBaladMap(container, apiKey, lat, lng, zoom, onSelect) {
  // Balad map SDK (developers.balad.ir)
  // Add <script src="https://balad.ir/api/mapkit/v4/mapkit.js" data-balad-token="API_KEY"></script>
  _initFallbackMap(container, lat, lng, onSelect); // placeholder until SDK is loaded
}

function _initFallbackMap(container, lat, lng, onSelect) {
  // Simple visual placeholder with manual coordinate input
  container.innerHTML = `
    <div class="map-placeholder" style="min-height:200px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; background:var(--bg-secondary); border-radius:var(--radius-md);">
      <i class="fa-solid fa-map-location-dot" style="font-size:2.5rem; color:var(--primary);"></i>
      <p style="font-size:0.82rem; color:var(--text-muted); text-align:center;">
        SDK نقشه در حال بارگذاری است...<br>یا آدرس را در فیلد بالا وارد کنید
      </p>
      <p style="font-size:0.72rem; color:var(--text-muted);">مختصات پیش‌فرض: ${lat}, ${lng}</p>
    </div>`;
  // Still call onSelect with default coords so the rest of the flow works
  onSelect({ lat, lng, address: '' });
}

async function _reverseGeocode(lat, lng, apiKey) {
  try {
    const res  = await fetch(
      `https://api.neshan.org/v5/reverse?lat=${lat}&lng=${lng}`,
      { headers: { 'Api-Key': apiKey } }
    );
    const data = await res.json();
    return data.formatted_address || '';
  } catch (_) { return ''; }
}
